import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useLessonWorkspace from '@/data/hook/lesson/useLessonWorkspace';
import {
  createLessonIntoBlock,
  updateLessonIntoBlock,
} from '@/data/services/lessonBlocksServices';
import Lesson from '@/models/Lesson';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';

import { adapterLessonToLessonDTO } from '@/utils/lesson/adapter';
import LessonForm, { LessonFormFields } from './LessonForm';
import { LessonDTO } from '@/data/services/lessonServices';
import cleanupObj from '@/utils/cleanupObj';

export type LessonFormServiceSuccess = {
  item: Lesson;
  action: 'create' | 'update';
};

type LessonFormServiceProps = {
  onSuccess?: (success: LessonFormServiceSuccess) => void;
};

const adapterLessonFieldsToDTO = (data: LessonFormFields): LessonDTO => {
  return {
    name: data.name,
    description: data.description,
    motivation: { message: data.objectives },
  };
};

export default function LessonFormService({
  onSuccess,
}: LessonFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonForm.alerts',
  });
  const { lesson, slug, unitId, lessonId, cache } = useLessonWorkspace();

  const handleCreate = async (data: LessonFormFields): Promise<Lesson> => {
    if (!slug) throw new Error('Slug not found');

    const payload = cleanupObj(adapterLessonFieldsToDTO(data)) as LessonDTO;

    const createdLesson = await createLessonIntoBlock(slug, unitId, payload);
    alert.success(t('createSuccess'));
    return createdLesson;
  };

  const handleUpdate = async (data: LessonFormFields): Promise<Lesson> => {
    if (!lesson || !slug) throw new Error('Lesson not found');
    const payload = adapterLessonFieldsToDTO(data);
    const lessonDto = adapterLessonToLessonDTO(lesson);
    const changedFields = buildChangedObject(lessonDto, payload);
    if (Object.keys(changedFields).length === 0) return lesson;
    const cleanedPayload = cleanupObj(payload);
    const updatedLesson = await updateLessonIntoBlock(
      slug,
      unitId,
      lessonId,
      cleanedPayload,
    );
    alert.success(t('updateSuccess'));
    return updatedLesson;
  };

  const handleSubmit = async (
    data: LessonFormFields,
  ): Promise<LessonFormServiceSuccess> => {
    if (!lesson) {
      const createdLesson = await handleCreate(data);
      return { item: createdLesson, action: 'create' };
    }
    const updatedLesson = await handleUpdate(data);
    return { item: updatedLesson, action: 'update' };
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async response => {
      cache?.merge(response.item);
      onSuccess?.(response);
    },
  });

  return (
    <LessonForm
      key={lesson?.id}
      lesson={lesson}
      onSubmit={onSubmit}
      isLoading={isPending}
    />
  );
}
